import React, { useMemo, useState } from 'react';
import type { VolumeMetrics, MuscleGroup } from '../types';
import { MUSCLE_GROUP_LABELS } from '../types';
import { volumeStatusColor, volumeStatusLabel } from '../utils/helpers';

interface Props {
  volume: VolumeMetrics;
}

/**
 * SVG Muscle Heatmap - front and back body views with color-coded muscle regions.
 */
export default function MuscleHeatmap({ volume }: Props) {
  const [hoveredMuscle, setHoveredMuscle] = useState<MuscleGroup | null>(null);

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const mv of volume.muscleVolumes) {
      map[mv.muscle] = volumeStatusColor(mv.status);
    }
    return map;
  }, [volume]);

  const getMuscleInfo = (muscle: MuscleGroup) => {
    const mv = volume.muscleVolumes.find((m) => m.muscle === muscle);
    if (!mv) return null;
    return mv;
  };

  const hoveredInfo = hoveredMuscle ? getMuscleInfo(hoveredMuscle) : null;

  const muscleProps = (muscle: MuscleGroup) => ({
    fill: colorMap[muscle] || '#94a3b8',
    fillOpacity: hoveredMuscle === muscle ? 1 : 0.7,
    stroke: hoveredMuscle === muscle ? '#1e293b' : 'none',
    strokeWidth: hoveredMuscle === muscle ? 1.5 : 0,
    className: 'cursor-pointer transition-all duration-200',
    onMouseEnter: () => setHoveredMuscle(muscle),
    onMouseLeave: () => setHoveredMuscle(null),
  });

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      <h3 className="text-sm font-bold mb-1">Mapa mięśni</h3>
      <p className="text-[10px] text-slate-400 mb-3">
        Najedź na region mięśniowy, aby zobaczyć szczegóły objętości
      </p>

      <div className="flex flex-col lg:flex-row items-start gap-4">
        {/* ── Body diagrams ─────────────────── */}
        <div className="flex gap-2 justify-center flex-1">
          {/* FRONT VIEW */}
          <div className="text-center">
            <span className="text-[10px] font-semibold text-slate-400 uppercase">Przód</span>
            <svg viewBox="0 0 200 400" className="w-[140px] h-[280px]">
              {/* Body outline */}
              <path
                d="M100,20 C115,20 120,35 120,45 L120,50 C135,52 155,60 158,72 L170,120 C172,130 168,140 162,148 L148,168 C146,172 142,174 140,178 L140,200 L130,200 L130,240 C132,260 138,280 140,310 C142,330 138,360 135,380 L125,380 C122,360 118,340 115,310 C112,290 108,260 107,240 L107,210 L93,210 L93,240 C92,260 88,290 85,310 C82,340 78,360 75,380 L65,380 C62,360 58,330 60,310 C62,280 68,260 70,240 L70,200 L60,200 L60,178 C58,174 54,172 52,168 L38,148 C32,140 28,130 30,120 L42,72 C45,60 65,52 80,50 L80,45 C80,35 85,20 100,20 Z"
                fill="#e2e8f0"
                className="dark:fill-slate-700"
                stroke="#cbd5e1"
                strokeWidth="0.5"
              />
              {/* Chest */}
              <ellipse cx="88" cy="90" rx="16" ry="14" {...muscleProps('chest')} />
              <ellipse cx="112" cy="90" rx="16" ry="14" {...muscleProps('chest')} />
              {/* Front Delts */}
              <ellipse cx="65" cy="72" rx="8" ry="10" {...muscleProps('front_delts')} />
              <ellipse cx="135" cy="72" rx="8" ry="10" {...muscleProps('front_delts')} />
              {/* Side Delts */}
              <ellipse cx="56" cy="68" rx="5" ry="8" {...muscleProps('side_delts')} />
              <ellipse cx="144" cy="68" rx="5" ry="8" {...muscleProps('side_delts')} />
              {/* Biceps */}
              <ellipse cx="52" cy="110" rx="7" ry="18" transform="rotate(-5 52 110)" {...muscleProps('biceps')} />
              <ellipse cx="148" cy="110" rx="7" ry="18" transform="rotate(5 148 110)" {...muscleProps('biceps')} />
              {/* Forearms */}
              <ellipse cx="46" cy="150" rx="5" ry="16" transform="rotate(-8 46 150)" {...muscleProps('forearms')} />
              <ellipse cx="154" cy="150" rx="5" ry="16" transform="rotate(8 154 150)" {...muscleProps('forearms')} />
              {/* Abs */}
              <rect x="86" y="108" width="28" height="42" rx="6" {...muscleProps('abs')} />
              {/* Hip Flexors */}
              <ellipse cx="90" cy="200" rx="10" ry="6" {...muscleProps('hip_flexors')} />
              <ellipse cx="110" cy="200" rx="10" ry="6" {...muscleProps('hip_flexors')} />
              {/* Quads */}
              <ellipse cx="88" cy="250" rx="14" ry="36" {...muscleProps('quads')} />
              <ellipse cx="112" cy="250" rx="14" ry="36" {...muscleProps('quads')} />
              {/* Calves (front) */}
              <ellipse cx="85" cy="335" rx="8" ry="24" {...muscleProps('calves')} />
              <ellipse cx="115" cy="335" rx="8" ry="24" {...muscleProps('calves')} />
            </svg>
          </div>

          {/* BACK VIEW */}
          <div className="text-center">
            <span className="text-[10px] font-semibold text-slate-400 uppercase">Tył</span>
            <svg viewBox="0 0 200 400" className="w-[140px] h-[280px]">
              {/* Body outline */}
              <path
                d="M100,20 C115,20 120,35 120,45 L120,50 C135,52 155,60 158,72 L170,120 C172,130 168,140 162,148 L148,168 C146,172 142,174 140,178 L140,200 L130,200 L130,240 C132,260 138,280 140,310 C142,330 138,360 135,380 L125,380 C122,360 118,340 115,310 C112,290 108,260 107,240 L107,210 L93,210 L93,240 C92,260 88,290 85,310 C82,340 78,360 75,380 L65,380 C62,360 58,330 60,310 C62,280 68,260 70,240 L70,200 L60,200 L60,178 C58,174 54,172 52,168 L38,148 C32,140 28,130 30,120 L42,72 C45,60 65,52 80,50 L80,45 C80,35 85,20 100,20 Z"
                fill="#e2e8f0"
                className="dark:fill-slate-700"
                stroke="#cbd5e1"
                strokeWidth="0.5"
              />
              {/* Traps */}
              <path d="M82,55 L100,48 L118,55 L112,72 L100,78 L88,72 Z" {...muscleProps('traps')} />
              {/* Rear Delts */}
              <ellipse cx="63" cy="72" rx="7" ry="8" {...muscleProps('rear_delts')} />
              <ellipse cx="137" cy="72" rx="7" ry="8" {...muscleProps('rear_delts')} />
              {/* Upper Back */}
              <ellipse cx="100" cy="90" rx="22" ry="16" {...muscleProps('upper_back')} />
              {/* Lats */}
              <path d="M72,85 L78,75 L82,90 L80,115 L72,118 Z" {...muscleProps('lats')} />
              <path d="M128,85 L122,75 L118,90 L120,115 L128,118 Z" {...muscleProps('lats')} />
              {/* Triceps */}
              <ellipse cx="52" cy="108" rx="6" ry="16" transform="rotate(-5 52 108)" {...muscleProps('triceps')} />
              <ellipse cx="148" cy="108" rx="6" ry="16" transform="rotate(5 148 108)" {...muscleProps('triceps')} />
              {/* Glutes */}
              <ellipse cx="90" cy="200" rx="16" ry="14" {...muscleProps('glutes')} />
              <ellipse cx="110" cy="200" rx="16" ry="14" {...muscleProps('glutes')} />
              {/* Hamstrings */}
              <ellipse cx="88" cy="250" rx="13" ry="32" {...muscleProps('hamstrings')} />
              <ellipse cx="112" cy="250" rx="13" ry="32" {...muscleProps('hamstrings')} />
              {/* Calves */}
              <ellipse cx="85" cy="330" rx="8" ry="22" {...muscleProps('calves')} />
              <ellipse cx="115" cy="330" rx="8" ry="22" {...muscleProps('calves')} />
            </svg>
          </div>
        </div>

        {/* ── Info panel ───────────────────── */}
        <div className="flex-1 min-w-[200px]">
          {hoveredInfo ? (
            <div className="animate-fade-in">
              <h4 className="font-bold text-sm mb-2">
                {MUSCLE_GROUP_LABELS[hoveredInfo.muscle]}
              </h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Serii bezpośrednich</span>
                  <span className="font-mono font-bold">{hoveredInfo.directSets}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Serii pośrednich</span>
                  <span className="font-mono font-bold">{hoveredInfo.indirectSets}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Ważona suma</span>
                  <span className="font-mono font-bold">{hoveredInfo.totalSets}</span>
                </div>
                <div className="h-px bg-slate-200 dark:bg-slate-700 my-1" />
                <div className="flex justify-between">
                  <span className="text-slate-500">MEV</span>
                  <span className="font-mono">{hoveredInfo.mev}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">MAV</span>
                  <span className="font-mono">{hoveredInfo.mav}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">MRV</span>
                  <span className="font-mono">{hoveredInfo.mrv}</span>
                </div>
                <div
                  className="mt-2 text-center py-1 rounded font-bold text-xs"
                  style={{
                    backgroundColor: volumeStatusColor(hoveredInfo.status) + '20',
                    color: volumeStatusColor(hoveredInfo.status),
                  }}
                >
                  {volumeStatusLabel(hoveredInfo.status)}
                </div>
              </div>

              {/* Mini bar */}
              <div className="mt-3">
                <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-1">
                  <span>0</span>
                  <div className="flex-1 h-2 rounded bg-slate-100 dark:bg-slate-800 relative overflow-hidden">
                    {/* MEV marker */}
                    <div
                      className="absolute top-0 h-full w-px bg-blue-400"
                      style={{ left: `${(hoveredInfo.mev / Math.max(hoveredInfo.mrv * 1.3, 1)) * 100}%` }}
                    />
                    {/* MAV marker */}
                    <div
                      className="absolute top-0 h-full w-px bg-green-400"
                      style={{ left: `${(hoveredInfo.mav / Math.max(hoveredInfo.mrv * 1.3, 1)) * 100}%` }}
                    />
                    {/* MRV marker */}
                    <div
                      className="absolute top-0 h-full w-px bg-red-400"
                      style={{ left: `${(hoveredInfo.mrv / Math.max(hoveredInfo.mrv * 1.3, 1)) * 100}%` }}
                    />
                    {/* Current volume */}
                    <div
                      className="absolute top-0 h-full rounded transition-all"
                      style={{
                        width: `${Math.min(100, (hoveredInfo.totalSets / Math.max(hoveredInfo.mrv * 1.3, 1)) * 100)}%`,
                        backgroundColor: volumeStatusColor(hoveredInfo.status),
                        opacity: 0.6,
                      }}
                    />
                  </div>
                  <span>{Math.round(hoveredInfo.mrv * 1.3)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-xs text-slate-400">
              Najedź na mięsień, aby zobaczyć szczegóły
            </div>
          )}

          {/* ── Quick summary table ─────── */}
          <div className="mt-4 max-h-[200px] overflow-y-auto">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-slate-400">
                  <th className="text-left py-0.5">Mięsień</th>
                  <th className="text-right py-0.5">Serie</th>
                  <th className="text-right py-0.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {volume.muscleVolumes
                  .filter((mv) => mv.directSets > 0 || mv.indirectSets > 0)
                  .map((mv) => (
                    <tr
                      key={mv.muscle}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                      onMouseEnter={() => setHoveredMuscle(mv.muscle)}
                      onMouseLeave={() => setHoveredMuscle(null)}
                    >
                      <td className="py-0.5 font-medium">{MUSCLE_GROUP_LABELS[mv.muscle]}</td>
                      <td className="py-0.5 text-right font-mono">{mv.totalSets}</td>
                      <td className="py-0.5 text-right">
                        <span
                          className="px-1 py-0.5 rounded text-[9px] font-bold"
                          style={{
                            backgroundColor: volumeStatusColor(mv.status) + '20',
                            color: volumeStatusColor(mv.status),
                          }}
                        >
                          {volumeStatusLabel(mv.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
        {[
          { label: 'Poniżej MEV', color: '#6366f1' },
          { label: 'Na MEV', color: '#3b82f6' },
          { label: 'Optymalnie', color: '#22c55e' },
          { label: 'Blisko MRV', color: '#f59e0b' },
          { label: 'Nad MRV', color: '#ef4444' },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
            <span className="text-[10px] text-slate-500">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
